import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Leistungen', path: '/leistungen' },
  { name: 'Pachtrechner', path: '/pachtrechner' },
  { name: 'Ratgeber', path: '/ratgeber' },
  { name: 'Über uns', path: '/ueber-uns' },
];

export const Header = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Progress from 0 to 1 over the first 80px of scroll
      const progress = Math.min(window.scrollY / 80, 1);
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isScrolled = scrollProgress > 0.1;

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: `rgba(255, 255, 255, ${scrollProgress * 0.92})`,
        backdropFilter: `blur(${scrollProgress * 16}px)`,
        WebkitBackdropFilter: `blur(${scrollProgress * 16}px)`,
        boxShadow: `0 1px 3px rgba(0, 0, 0, ${scrollProgress * 0.08})`,
        borderBottom: `1px solid rgba(226, 232, 240, ${scrollProgress})`,
      }}
    >
      {/* Top bar — collapses smoothly on scroll */}
      <div
        className="hidden md:block border-b border-slate-200/50"
        style={{
          maxHeight: `${(1 - scrollProgress) * 40}px`,
          opacity: 1 - scrollProgress,
          overflow: 'hidden',
          backgroundColor: scrollProgress < 0.5 ? 'rgba(255,255,255,0.8)' : `rgba(249,250,251,${scrollProgress})`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+491608732681" className="flex items-center gap-2 text-slate-600 hover:text-[#E07000] transition-colors">
                <Phone className="w-4 h-4" />
                <span>+49 160 8732681</span>
              </a>
              <a href="mailto:info@en-power.eu" className="flex items-center gap-2 text-slate-600 hover:text-[#E07000] transition-colors">
                <Mail className="w-4 h-4" />
                <span>info@en-power.eu</span>
              </a>
            </div>
            <div className="text-slate-500">
              Ihr Partner für erneuerbare Energien
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 md:py-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
            aria-label="Zur Startseite von EN:POWER (ENPOWER)"
          >
            <img
              src="/assets/ENPOWER-logo.png"
              alt="EN:POWER Logo ENPOWER"
              width={1120}
              height={160}
              data-testid="header-logo-img"
              fetchPriority="high"
              decoding="async"
              className="h-8 md:h-9 lg:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`nav-link font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#122A5C] active'
                    : 'text-slate-600 hover:text-[#122A5C]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/angebot-anfordern">
              <Button
                data-testid="header-cta-button"
                className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-8 py-3 font-semibold btn-animate shadow-lg shadow-orange-500/25"
              >
                Angebot anfordern
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Menue schliessen" : "Menue oeffnen"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            className="lg:hidden p-2 text-[#122A5C]"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200 shadow-lg"
            data-testid="mobile-menu"
            id="mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`block py-2 font-medium text-lg ${
                    location.pathname === link.path
                      ? 'text-[#E07000]'
                      : 'text-slate-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/angebot-anfordern" className="block pt-4">
                <Button 
                  data-testid="mobile-cta-button"
                  className="w-full bg-[#E07000] hover:bg-[#C56200] text-white rounded-full py-3 font-semibold"
                >
                  Angebot anfordern
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
